/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Button,
  Spinner,
  Alert,
  FormGroup,
  Label,
  UncontrolledTooltip
} from 'reactstrap';
import {
  Trash,
  PersonCircle,
  JournalText,
  ReceiptCutoff,
  CheckLg,
  Check2Circle
} from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import ImageUploading from 'react-images-uploading';
import GInput from '../../components/Input';
import SimpleHeader from '../../components/Headers/SimpleHeader';
import client from '../../utils/client';
import { regexEmail, regexUrl } from '../../utils/regex';
import { getCountry } from '../../utils/helpers';

const country = getCountry();

const ClubInformation = () => {
  const { t } = useTranslation();

  const [inProgress, setInProgress] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [images, setImages] = useState([]);
  const [imageData, setImageData] = useState({ images: [] });

  const {
    handleSubmit,
    setValue,
    register,
    formState: { errors }
  } = useForm();

  useEffect(() => {
    (async () => {
      setInProgress(true);
      const { data, error: clubInfoError } = await client('admin-gc/club-info');

      if (!clubInfoError) {
        setImageData(data.album);
        Object.keys(data).forEach((key) => {
          setValue(key, data[key]);
        });
        setInProgress(null);
      } else {
        setError(clubInfoError.message);
      }
    })();
  }, []);

  const onChange = (imageList) => {
    setError(null);
    setImages(imageList);
  };

  const onSubmit = async (formData) => {
    setError(null);
    setSuccess(null);
    setInProgress('save');
    const { error: err } = await client('admin-gc/club-info', { body: formData });

    if (err) {
      setError(err.message);
    } else {
      setSuccess(true);
    }
    setInProgress(null);
  };

  const onImageSave = async () => {
    setInProgress('images');

    const imagesToSave = new FormData();
    images.forEach((img, i) => {
      imagesToSave.append(i, img.file);
    });

    const { error: imageError } = await client('admin-gc/club-info/images', { body: imagesToSave });
    if (!imageError) {
      window.location.reload();
    } else {
      setError(imageError.message);
      setInProgress(null);
    }
  };

  const onImageDelete = async (filePath) => {
    const fileName = filePath.substring(filePath.lastIndexOf('/') + 1);
    setInProgress(`image-delete-${fileName}`);
    const { error: imageError } = await client(
      'admin-gc/club-info/images',
      { body: { fileName } },
      'DELETE'
    );
    if (!imageError) {
      setImageData({ ...imageData, images: imageData.images?.filter((i) => i !== fileName) });
    }
    setInProgress(null);
  };

  const onImagePrimary = async (filePath) => {
    const fileName = filePath.substring(filePath.lastIndexOf('/') + 1);
    setInProgress(`image-primary-${fileName}`);
    const { error: imageError } = await client('admin-gc/club-info/images/primary', {
      body: { fileName }
    });

    if (!imageError) {
      setImageData({
        ...imageData,
        images: imageData.images?.sort((a, b) => {
          if (a === filePath) return -1;
          if (b === filePath) return 1;
          return 0;
        })
      });
    }
    setInProgress(null);
  };

  return (
    <>
      <SimpleHeader name={t('club-information.title')} />
      <Container className="mt--6" fluid>
        <Row>
          <Col>
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3 className="mb-0">{t('club-information.sub-title')}</h3>
                </CardHeader>
                <CardBody>
                  {error && (
                    <Alert color="danger" className="mb-3">
                      {error}
                    </Alert>
                  )}
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <Row>
                      <Col>
                        <h3 className="bold mt-3">
                          <JournalText className="mb-1 mr-2" />
                          {t('club-information.club-information')}
                        </h3>
                        <Row>
                          <Col xs="12" sm="4">
                            <FormGroup>
                              <Label>{t('default.address')}</Label>
                              <GInput
                                name="clubAddress"
                                register={register}
                                rules={{
                                  required: true,
                                  minLength: 3
                                }}
                                error={!!errors.clubAddress}
                              />
                            </FormGroup>
                          </Col>
                          <Col xs="12" sm="4">
                            <FormGroup>
                              <Label>{t('default.post-code')}</Label>
                              <GInput
                                name="clubPostCode"
                                register={register}
                                rules={{
                                  required: true,
                                  minLength: 4
                                }}
                                error={!!errors.clubPostCode}
                              />
                            </FormGroup>
                          </Col>
                          <Col xs="12" sm="4">
                            <FormGroup>
                              <Label>{t('default.post-address')}</Label>
                              <GInput
                                name="clubPostAddress"
                                register={register}
                                rules={{
                                  required: true,
                                  minLength: 2
                                }}
                                error={!!errors.clubPostAddress}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col xs="12" sm="6">
                            <FormGroup>
                              <Label>{t('club-information.phone-reception')}</Label>
                              <GInput
                                name="recPhone"
                                register={register}
                                rules={{
                                  required: true,
                                  minLength: 8
                                }}
                                error={!!errors.recPhone}
                              />
                            </FormGroup>
                          </Col>
                          <Col xs="12" sm="6">
                            <FormGroup>
                              <Label>{t('club-information.phone-booking')}</Label>
                              <GInput
                                name="bookingPhone"
                                register={register}
                                rules={{
                                  required: true,
                                  minLength: 8
                                }}
                                error={!!errors.bookingPhone}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col xs="12" sm="6">
                            <FormGroup>
                              <Label>{t('default.email')}</Label>
                              <GInput
                                name="email"
                                type="email"
                                register={register}
                                rules={{ required: true, pattern: regexEmail }}
                                error={!!errors.email}
                              />
                            </FormGroup>
                          </Col>
                          <Col xs="12" sm="6">
                            <FormGroup>
                              <Label>
                                <Label>{t('default.website')}</Label>{' '}
                                <span className="small">{t('default.website-help')}</span>
                              </Label>
                              <GInput
                                name="homepage"
                                type="url"
                                register={register}
                                rules={{ required: true, pattern: regexUrl }}
                                error={!!errors.homepage}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <h3 className="bold mt-3">
                          <PersonCircle className="mb-1 mr-2" />
                          {t('club-information.contact-person')}
                        </h3>
                        <Row>
                          <Col xs="12" sm="4">
                            <FormGroup>
                              <Label>{t('default.firstname')}</Label>
                              <GInput
                                name="contactFirstname"
                                register={register}
                                rules={{ required: true, minLength: 2 }}
                                error={!!errors.contactFirstname}
                              />
                            </FormGroup>
                          </Col>
                          <Col xs="12" sm="4">
                            <FormGroup>
                              <Label>{t('default.lastname')}</Label>
                              <GInput
                                name="contactLastname"
                                register={register}
                                rules={{ required: true, minLength: 2 }}
                                error={!!errors.contactLastname}
                              />
                            </FormGroup>
                          </Col>
                          <Col xs="12" sm="4">
                            <FormGroup>
                              <Label>{t('default.email')}</Label>
                              <GInput
                                name="contactEmail"
                                type="email"
                                register={register}
                                rules={{ required: true, pattern: regexEmail }}
                                error={!!errors.contactEmail}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col xs="12" sm="6">
                            <FormGroup>
                              <Label>{t('default.phone')}</Label>
                              <GInput
                                name="contactPhone"
                                type="tel"
                                register={register}
                                rules={{ required: true, minLength: 8 }}
                                error={!!errors.contactPhone}
                              />
                            </FormGroup>
                          </Col>
                          <Col xs="12" sm="6">
                            <FormGroup>
                              <Label>{t('default.mobile')}</Label>
                              <GInput
                                name="contactMobile"
                                type="tel"
                                register={register}
                                rules={{ required: true, minLength: 8 }}
                                error={!!errors.contactMobile}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                    <h3 className="bold mt-3">
                      <ReceiptCutoff className="mb-1 mr-2" />
                      <Label>{t('club-information.invoice-information')}</Label>
                    </h3>
                    <Row>
                      <Col>
                        <Row>
                          <Col xs="12" sm="6">
                            <FormGroup>
                              <Label>{t('club-information.company-name')}</Label>
                              <GInput
                                name="iName"
                                register={register}
                                rules={{ required: true, minLength: 3 }}
                                error={!!errors.iName}
                              />
                            </FormGroup>
                          </Col>
                          <Col xs="12" sm="6">
                            <FormGroup>
                              <Label>
                                {t('default.vat-number')}{' '}
                                <span className="small mb-0">{t('default.vat-number-help')}</span>
                              </Label>
                              <GInput
                                name="vatNo"
                                register={register}
                                // rules={{ required: true, minLength: 3 }}
                                error={!!errors.vatNo}
                              />
                              {country === 'DK' && (
                                <span className="small d-block">
                                  Er klubben fritaget for moms, skal feltet stå tomt
                                </span>
                              )}
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col xs="12" sm="4">
                            <FormGroup>
                              <Label>{t('default.address')}</Label>
                              <GInput
                                name="invAddress"
                                register={register}
                                rules={{ required: true, minLength: 3 }}
                                error={!!errors.invAddress}
                              />
                            </FormGroup>
                          </Col>
                          <Col xs="12" sm="4">
                            <FormGroup>
                              <Label>{t('default.post-code')}</Label>
                              <GInput
                                name="invPostCode"
                                register={register}
                                rules={{ required: true, minLength: 3 }}
                                error={!!errors.invPostCode}
                              />
                            </FormGroup>
                          </Col>
                          <Col xs="12" sm="4">
                            <FormGroup>
                              <Label>{t('default.post-address')}</Label>
                              <GInput
                                name="invPostAddress"
                                register={register}
                                rules={{ required: true, minLength: 3 }}
                                error={!!errors.invPostAddress}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <h3 className="bold mt-3">
                          <PersonCircle className="mb-1 mr-2" />
                          <Label>{t('club-information.contact-person-economy')}</Label>
                        </h3>
                        <Row>
                          <Col xs="12" sm="4">
                            <FormGroup>
                              <Label>{t('default.firstname')}</Label>
                              <GInput
                                name="invContactFirstname"
                                register={register}
                                rules={{ required: true, minLength: 2 }}
                                error={!!errors.invContactFirstname}
                              />
                            </FormGroup>
                          </Col>
                          <Col xs="12" sm="4">
                            <FormGroup>
                              <Label>{t('default.lastname')}</Label>
                              <GInput
                                name="invContactLastname"
                                register={register}
                                rules={{ required: true, minLength: 2 }}
                                error={!!errors.invContactLastname}
                              />
                            </FormGroup>
                          </Col>
                          <Col xs="12" sm="4">
                            <FormGroup>
                              <Label>{t('default.email')}</Label>
                              <GInput
                                name="invContactEmail"
                                register={register}
                                rules={{ required: true, pattern: regexEmail }}
                                error={!!errors.invContactEmail}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col xs="12" sm="6">
                            <FormGroup>
                              <Label>{t('default.phone')}</Label>
                              <GInput
                                name="invContactPhone"
                                register={register}
                                rules={{ required: true, minLength: 8 }}
                                error={!!errors.invContactPhone}
                              />
                            </FormGroup>
                          </Col>
                          <Col xs="12" sm="6">
                            <FormGroup>
                              <Label>{t('default.mobile')}</Label>
                              <GInput
                                name="invContactMobile"
                                register={register}
                                rules={{ required: true, minLength: 8 }}
                                error={!!errors.invContactMobile}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        {error && (
                          <Alert color="danger" className="mb-3">
                            {error}
                          </Alert>
                        )}
                        {success && (
                          <Alert color="success">
                            <CheckLg className="mr-2" size={23} />
                            {t('default.update-information-confirmed')}
                          </Alert>
                        )}
                        <Row>
                          <Col className="text-align-right">
                            <Button type="submit" color="success" disabled={inProgress === 'save'}>
                              {inProgress === 'save' && <Spinner size="sm" />}
                              {t('default.update-information')}
                            </Button>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </form>
                </CardBody>
              </Card>
            </div>
          </Col>
        </Row>
        <Row>
          <Col>
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3 className="mb-0">{t('club-information.images')}</h3>
                </CardHeader>
                <CardBody>
                  <Row>
                    {imageData?.images.map((i, key) => (
                      // <img src={formData.album.path+a} />
                      <Col xs="12" sm="4" className="p-2" key={i}>
                        <img
                          src={imageData.path + i}
                          alt={i}
                          style={{ width: '100%', border: '1pt solid #000' }}
                        />
                        <br />
                        <Button
                          color="link"
                          className="only-icon"
                          disabled={inProgress === `image-delete${i}`}
                        >
                          {inProgress === `image-delete-${i}` && <Spinner size="sm" />}
                          <Trash onClick={() => onImageDelete(i)} color="red" />
                        </Button>
                        {key === 0 ? (
                          <span className="bold">
                            {t('club-information.image-primary', 'Primär bild')}
                          </span>
                        ) : (
                          <>
                            <span href="#" id={`image-primary-${key}`}>
                              <Button
                                color="link"
                                className="only-icon"
                                disabled={inProgress === `image-primary${i}`}
                              >
                                {inProgress === `image-primary-${i}` && <Spinner size="sm" />}
                                <Check2Circle onClick={() => onImagePrimary(i)} color="green" />
                              </Button>
                            </span>
                            <UncontrolledTooltip target={`image-primary-${key}`}>
                              {t('club-information.image-primary-show', 'Visa som primär bild')}
                            </UncontrolledTooltip>
                          </>
                        )}
                      </Col>
                    ))}
                  </Row>
                  <ImageUploading
                    multiple
                    value={images}
                    onChange={onChange}
                    maxNumber={1}
                    dataURLKey="data_url"
                  >
                    {({ imageList, onImageUpload, onImageRemove, isDragging, dragProps }) => (
                      <>
                        <div className="upload__image-wrapper">
                          <Button
                            color="secondary"
                            style={isDragging ? { color: '#000' } : undefined}
                            onClick={onImageUpload}
                            className="mb-3 mt-3"
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            {...dragProps}
                          >
                            {t('club-information.image-click')}
                          </Button>
                          {imageList.map((image, index) => (
                            <div key={image.file.size} className="image-item">
                              <img src={image.data_url} alt="" width="100" />
                              <div className="image-item__btn-wrapper">
                                <Button
                                  color="danger"
                                  onClick={() => onImageRemove(index)}
                                  className="mt-2"
                                >
                                  {t('club-information.image-delete')}
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                        {error && <Alert color="danger mt-3">{error}</Alert>}
                        {imageList?.length > 0 && (
                          <Row>
                            <Col className="text-align-right">
                              <Button
                                color="success"
                                onClick={onImageSave}
                                disabled={inProgress === 'images'}
                              >
                                {inProgress === 'images' && <Spinner size="sm" className="mr-2" />}
                                {t('club-information.image-save')}
                              </Button>
                            </Col>
                          </Row>
                        )}
                      </>
                    )}
                  </ImageUploading>
                </CardBody>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default ClubInformation;
